import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Inbox, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const TOKEN_KEY = "ktr_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [fetching, setFetching] = useState(false);

  const load = useCallback(async (t) => {
    setFetching(true);
    try {
      const res = await api.get("/inquiries", { headers: { "X-Admin-Token": t } });
      setInquiries(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load inquiries.");
      }
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]);
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-secondary-foreground/70 hover:text-white mb-10 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <h1 className="font-heading font-black tracking-tighter text-4xl mb-2">
            Admin<span className="text-flame">.</span>
          </h1>
          <p className="text-secondary-foreground/70 mb-10">Enter your password to view inquiries.</p>
          <form onSubmit={login} className="space-y-8">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-transparent border-0 border-b border-line focus:border-flame focus:outline-none py-4 text-lg placeholder:text-secondary-foreground/40"
              data-testid="admin-password-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-3 bg-flame hover:bg-[#E64500] disabled:opacity-60 text-ink font-semibold px-8 py-4 transition-colors w-full justify-center"
              data-testid="admin-login-button"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              Sign in
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-line sticky top-0 bg-ink/80 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-heading font-extrabold tracking-tighter text-lg">
            KTR<span className="text-flame">.</span>ADMIN
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(token)}
              className="inline-flex items-center gap-2 border border-line hover:border-flame px-4 py-2 text-sm transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${fetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 border border-line hover:border-flame px-4 py-2 text-sm transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-10">
          <h1 className="font-heading font-black tracking-tighter text-3xl md:text-5xl">
            Inquiries <span className="text-flame">({inquiries.length})</span>
          </h1>
        </div>

        {fetching && inquiries.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-flame" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 border border-line text-secondary-foreground/60" data-testid="admin-empty">
            <Inbox className="h-12 w-12 mb-4" />
            <p>No inquiries yet.</p>
          </div>
        ) : (
          <div className="border border-line divide-y divide-line" data-testid="admin-inquiry-list">
            {inquiries.map((q) => (
              <div key={q.id} className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 hover:bg-elevated transition-colors" data-testid={`inquiry-${q.id}`}>
                <div className="md:col-span-3">
                  <p className="font-heading font-bold text-xl">{q.name}</p>
                  <a href={`mailto:${q.email}`} className="text-flame text-sm break-all">{q.email}</a>
                  {q.phone ? <p className="text-secondary-foreground/70 text-sm mt-1">{q.phone}</p> : null}
                </div>
                <div className="md:col-span-2">
                  {q.service ? (
                    <span className="inline-block border border-line px-3 py-1 text-xs uppercase tracking-wider">{q.service}</span>
                  ) : (
                    <span className="text-secondary-foreground/40 text-xs">—</span>
                  )}
                </div>
                <p className="md:col-span-5 text-secondary-foreground/85">{q.message}</p>
                <p className="md:col-span-2 text-secondary-foreground/50 text-sm md:text-right">
                  {new Date(q.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
